import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  Table,
  Form,
  Button,
  Modal,
  Row,
  Col,
  Pagination,
  Container,
  Spinner,
  Badge,
} from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import type {
  QuestionnaireTemplate,
  UserOrganisation,
} from '../../types/types';
import NewQuestionnaireModal from './NewQuestionnaireModal';
import { captureSupabaseError } from '../../lib/sentry';
import { tracking } from '../../util/tracking';

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function QuestionnaireList({
  reloadKey,
  onSendToSupplier,
}: {
  reloadKey: number;
  onSendToSupplier: (templateId: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQueryParams();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  ) as UserOrganisation | undefined;

  const [search, setSearch] = useState(query.get('search') || '');
  const [page, setPage] = useState(Number(query.get('page') || 1));
  const [sortBy, setSortBy] = useState<'title' | 'updated_at' | 'status'>(
    (query.get('sortBy') as 'title' | 'updated_at' | 'status') || 'updated_at'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (query.get('sortDirection') as 'asc' | 'desc') || 'desc'
  );
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [localReloadKey, setLocalReloadKey] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showNewQuestionnaireModal, setShowNewQuestionnaireModal] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireTemplate[]>(
    []
  );
  const [total, setTotal] = useState(0);

  const pageSize = 10;
  const effectiveReloadKey = reloadKey + localReloadKey;

  // Sync query params with state
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (page > 1) params.set('page', String(page));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDirection) params.set('sortDirection', sortDirection);

    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
  }, [search, page, sortBy, sortDirection]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search) setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch questionnaires
  useEffect(() => {
    if (!selectedOrganisation?.organisation?.id) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let query = supabase
          .from('questionnaire_templates')
          .select('*', { count: 'exact' })
          .eq('organisation_id', selectedOrganisation?.organisation?.id);

        if (debouncedSearch) {
          query = query.ilike('title', `%${debouncedSearch}%`);
        }

        query = query.order(sortBy, { ascending: sortDirection === 'asc' });
        query = query.range((page - 1) * pageSize, page * pageSize - 1);

        const { data, error, count } = await query;

        if (error) throw error;

        setQuestionnaires(data ?? []);
        setTotal(count ?? 0);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        captureSupabaseError(err, { op: 'questionnaires.fetch' });
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedOrganisation?.organisation?.id,
    effectiveReloadKey,
    debouncedSearch,
    page,
    sortBy,
    sortDirection,
  ]);

  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (column: 'title' | 'updated_at' | 'status') => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: 'title' | 'updated_at' | 'status') => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('questionnaire_templates')
        .delete()
        .in('id', selectedIds);

      if (error) throw error;

      setShowDeleteModal(false);
      setSelectedIds([]);
      setLocalReloadKey((prev) => prev + 1);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'questionnaires.delete' });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedOrganisation?.organisation) {
    return <div>Please select an organisation to view questionnaires.</div>;
  }

  return (
    <div>
      <Row>
        <Col>
          <Button
            className="ms-2"
            variant="primary"
            onClick={() => {
              tracking.log('UI', 'questionnaire_new', {
                page: 'questionnaires',
              });
              setShowNewQuestionnaireModal(true);
            }}
          >
            + New Questionnaire
          </Button>
        </Col>
      </Row>

      <Row className="my-2 align-items-center">
        <Col className="d-flex">
          {selectedIds.length > 0 && (
            <Button
              variant="danger"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
              className="me-2"
            >
              Delete ({selectedIds.length})
            </Button>
          )}
        </Col>
        <Col className="d-flex justify-content-end">
          <Form.Control
            type="text"
            placeholder="Search questionnaires..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: '250px' }}
          />
        </Col>
      </Row>

      {loading && (
        <Container fluid className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </Container>
      )}

      {error && <div className="text-danger">Error: {error}</div>}

      {!loading && questionnaires.length === 0 && (
        <div>No questionnaires found.</div>
      )}

      {questionnaires.length > 0 && (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={
                      selectedIds.length === questionnaires.length &&
                      questionnaires.length > 0
                    }
                    onChange={(e) =>
                      setSelectedIds(
                        e.target.checked ? questionnaires.map((q) => q.id) : []
                      )
                    }
                  />
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('title')}
                >
                  Title {renderSortIcon('title')}
                </th>
                <th>Description</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('status')}
                >
                  Status {renderSortIcon('status')}
                </th>
                <th>Actions</th>
                <th>Version</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('updated_at')}
                >
                  Updated {renderSortIcon('updated_at')}
                </th>
              </tr>
            </thead>
            <tbody>
              {questionnaires.map((q) => (
                <tr
                  key={q.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(`/questionnaires/${q.id}/edit`, {
                      state: { from: location },
                    })
                  }
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedIds.includes(q.id)}
                      onChange={() => toggleSelection(q.id)}
                    />
                  </td>
                  <td>{q.title}</td>
                  <td>{q.description || '-'}</td>
                  <td>
                    <Badge
                      bg={
                        q.status === 'draft'
                          ? 'secondary'
                          : q.status === 'published'
                          ? 'success'
                          : 'light'
                      }
                    >
                      {q.status}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        tracking.log('UI', 'questionnaire_send_open', {
                          templateId: q.id,
                          page: 'questionnaires_list',
                        });
                        onSendToSupplier(q.id);
                      }}
                    >
                      Send
                    </Button>
                  </td>
                  <td>{q.version}</td>
                  <td>{dayjs(q.updated_at).format('DD.MM.YYYY HH:mm')}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.First
                disabled={page === 1}
                onClick={() => setPage(1)}
              />
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === page}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              />
              <Pagination.Last
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
              />
            </Pagination>
          )}
        </>
      )}

      {/* Delete confirmation modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete {selectedIds.length} questionnaire
            {selectedIds.length > 1 ? 's' : ''}?
          </p>
          <p className="text-danger">You cannot undo this operation.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      <NewQuestionnaireModal
        show={showNewQuestionnaireModal}
        onHide={() => setShowNewQuestionnaireModal(false)}
      />
    </div>
  );
}
